// hardcoded-ui-text: allow — the description column of the settings rows this service writes
// to the database; no screen renders them back.
import { supabase } from '@/integrations/supabase/client';
import { eventBus } from '../events/EventBus';
import { moduleRegistry } from '../registry/ModuleRegistry';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'OFF';

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  moduleOrChannel?: string;
  category?: string;
  traceId?: string;
}

export interface LoggingConfig {
  enabled: boolean;
  globalLevel: LogLevel;
  consoleOutput: boolean;
  databaseLogging: boolean;
  externalServices: boolean;
  logRetentionDays: number;
}

export interface ChannelSettings {
  enabled: boolean;
  level: LogLevel;
  [key: string]: unknown;
}

export interface CategoryLevels {
  [module: string]: LogLevel;
}

export class LoggingService {
  private static instance: LoggingService;
  private tenantId: string | null = null;
  private userId: string | null = null;
  private config: LoggingConfig | null = null;
  private categoryLevels: CategoryLevels = {};
  private channelSettings: Record<string, ChannelSettings> = {};
  private platformConfig: unknown = null;
  private registeredModules = new Set<string>();
  private registeredChannels = new Set<string>();
  private pendingLogs: LogEntry[] = []; // Queue for logs when tenant context isn't ready

  private constructor() {
    this.initializeEventListeners();
    this.autoDiscoverModules();
  }

  public static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  // Auto-discover modules from moduleRegistry
  private autoDiscoverModules() {
    // Listen for new module registrations
    eventBus.on('module:registered', (payload) => {
      if (payload.name) {
        this.registerModule(payload.name);
      }
    });
  }

  // Register a new module for logging
  public registerModule(moduleName: string) {
    if (!this.registeredModules.has(moduleName)) {
      this.registeredModules.add(moduleName);
      this.autoConfigureModule(moduleName);
      console.log(`[LoggingService] Auto-registered module: ${moduleName}`);
    }
  }

  // Register a communication channel
  public registerChannel(channelName: string, defaultSettings?: Partial<ChannelSettings>) {
    if (!this.registeredChannels.has(channelName)) {
      this.registeredChannels.add(channelName);
      this.autoConfigureChannel(channelName, defaultSettings);
      console.log(`[LoggingService] Auto-registered channel: ${channelName}`);
    }
  }

  // Auto-configure a new module with default settings
  private async autoConfigureModule(moduleName: string) {
    if (!this.tenantId) return;

    try {
      // Check if module already has configuration
      const { data: existing } = await supabase
        .from('tenant_settings')
        .select('setting_value')
        .eq('tenant_id', this.tenantId)
        .eq('setting_key', 'logging_category_levels')
        .single();

      const currentLevels = existing?.setting_value || {};
      
      // Add default level for new module if not exists
      if (!currentLevels[moduleName]) {
        currentLevels[moduleName] = this.platformConfig?.default_log_level || 'INFO';
        
        await supabase
          .from('tenant_settings')
          .upsert({
            tenant_id: this.tenantId,
            setting_key: 'logging_category_levels',
            setting_value: currentLevels,
            description: 'Per-module log level configuration'
          });
      }
    } catch (error) {
      console.warn(`Failed to auto-configure module ${moduleName}:`, error);
    }
  }

  // Auto-configure a new channel with default settings
  private async autoConfigureChannel(channelName: string, defaultSettings?: Partial<ChannelSettings>) {
    if (!this.tenantId) return;

    try {
      const { data: existing } = await supabase
        .from('tenant_settings')
        .select('setting_value')
        .eq('tenant_id', this.tenantId)
        .eq('setting_key', 'logging_channel_settings')
        .single();

      const currentSettings = existing?.setting_value || {};
      
      if (!currentSettings[channelName]) {
        currentSettings[channelName] = {
          enabled: true,
          level: this.platformConfig?.default_log_level || 'INFO',
          ...defaultSettings
        };
        
        await supabase
          .from('tenant_settings')
          .upsert({
            tenant_id: this.tenantId,
            setting_key: 'logging_channel_settings',
            setting_value: currentSettings,
            description: 'Communication channel specific logging settings'
          });
      }
    } catch (error) {
      console.warn(`Failed to auto-configure channel ${channelName}:`, error);
    }
  }

  // Initialize event listeners for module system integration
  private initializeEventListeners() {
    // Listen for auth events to set user context
    eventBus.on('auth:signIn', (payload) => {
      this.setUserContext(payload.user?.id);
    });

    eventBus.on('auth:signOut', () => {
      this.clearUserContext();
    });

    // Listen for tenant context changes
    eventBus.on('tenant:selected', (payload) => {
      this.setTenantContext(payload.tenantId);
    });
  }

  // Set user context for logging
  public setUserContext(userId: string) {
    this.userId = userId;
    this.loadConfiguration();
  }

  // Set tenant context for logging
  public setTenantContext(tenantId: string) {
    this.tenantId = tenantId;
    this.loadConfiguration();
    this.flushPendingLogs(); // Process any queued logs now that we have tenant context
  }

  // Clear user context
  public clearUserContext() {
    this.userId = null;
    this.tenantId = null;
    this.config = null;
    this.categoryLevels = {};
    this.channelSettings = {};
  }

  // Load logging configuration for current tenant
  private async loadConfiguration() {
    if (!this.tenantId) return;

    try {
      // Load platform configuration
      const { data: platformData } = await supabase
        .from('platform_logging_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      this.platformConfig = platformData;

      // Load tenant configuration
      const { data: tenantSettings } = await supabase
        .from('tenant_settings')
        .select('setting_key, setting_value')
        .eq('tenant_id', this.tenantId)
        .in('setting_key', [
          'logging_configuration',
          'logging_category_levels', 
          'logging_channel_settings',
          'logging_external_outputs'
        ]);

      // Process tenant settings
      tenantSettings?.forEach(setting => {
        switch (setting.setting_key) {
          case 'logging_configuration':
            this.config = setting.setting_value as unknown as LoggingConfig;
            break;
          case 'logging_category_levels':
            this.categoryLevels = (setting.setting_value as unknown as CategoryLevels) || {};
            break;
          case 'logging_channel_settings':
            this.channelSettings = (setting.setting_value as unknown as Record<string, ChannelSettings>) || {};
            break;
        }
      });

      // Set defaults if no configuration exists
      if (!this.config) {
        this.config = {
          enabled: this.platformConfig?.global_enabled ?? true,
          globalLevel: this.platformConfig?.default_log_level ?? 'INFO',
          consoleOutput: this.platformConfig?.console_output_enabled ?? true,
          databaseLogging: this.platformConfig?.database_logging_enabled ?? false,
          externalServices: this.platformConfig?.external_service_enabled ?? false,
          logRetentionDays: this.platformConfig?.max_log_retention_days ?? 14
        };
      }
    } catch (error) {
      console.warn('Failed to load logging configuration:', error);
      // Set safe defaults
      this.config = {
        enabled: true,
        globalLevel: 'INFO',
        consoleOutput: true,
        databaseLogging: false,
        externalServices: false,
        logRetentionDays: 14
      };
    }
  }

  // Check if logging is enabled for a specific level and context
  private shouldLog(level: LogLevel, moduleOrChannel?: string): boolean {
    if (!this.config?.enabled || !this.platformConfig?.global_enabled) {
      return false;
    }

    const levels: LogLevel[] = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'OFF'];
    const levelIndex = levels.indexOf(level);
    
    // Check module/channel specific level
    if (moduleOrChannel) {
      const specificLevel = this.categoryLevels[moduleOrChannel] || 
                           this.channelSettings[moduleOrChannel]?.level;
      if (specificLevel) {
        const specificLevelIndex = levels.indexOf(specificLevel);
        return levelIndex >= specificLevelIndex && specificLevel !== 'OFF';
      }
    }

    // Check global level
    const globalLevelIndex = levels.indexOf(this.config.globalLevel);
    return levelIndex >= globalLevelIndex && this.config.globalLevel !== 'OFF';
  }

  // Main logging method
  public log(entry: LogEntry) {
    if (!this.shouldLog(entry.level, entry.moduleOrChannel)) {
      return;
    }

    // If no tenant context yet, queue the log for later processing
    if (!this.tenantId && this.config?.databaseLogging) {
      this.pendingLogs.push(entry);
      // Still allow console output immediately
      if (this.config?.consoleOutput) {
        this.logToConsole({
          ...entry,
          timestamp: new Date().toISOString(),
          tenantId: null,
          userId: this.userId,
          traceId: entry.traceId || this.generateTraceId()
        });
      }
      return;
    }

    const logData = {
      ...entry,
      timestamp: new Date().toISOString(),
      tenantId: this.tenantId,
      userId: this.userId,
      traceId: entry.traceId || this.generateTraceId()
    };

    // Console output
    if (this.config?.consoleOutput) {
      this.logToConsole(logData);
    }

    // Database logging
    if (this.config?.databaseLogging && this.platformConfig?.database_logging_enabled) {
      this.logToDatabase(logData);
    }

    // External services (future)
    if (this.config?.externalServices && this.platformConfig?.external_service_enabled) {
      this.logToExternalServices(logData);
    }
  }

  // Flush pending logs when tenant context becomes available
  private flushPendingLogs() {
    if (this.pendingLogs.length === 0 || !this.tenantId) {
      return;
    }

    console.log(`[LoggingService] Flushing ${this.pendingLogs.length} pending logs with tenant context`);
    
    const logsToFlush = [...this.pendingLogs];
    this.pendingLogs = []; // Clear the queue

    // Process each pending log with the now-available tenant context
    logsToFlush.forEach(entry => {
      this.log(entry);
    });
  }

  // Console logging with formatting
  private logToConsole(logData: Record<string, unknown>) {
    const prefix = `[${logData.timestamp}] [${logData.level}] [${logData.moduleOrChannel || 'SYSTEM'}]`;
    const message = `${prefix} ${logData.message}`;
    
    switch (logData.level) {
      case 'DEBUG':
        console.debug(message, logData.context);
        break;
      case 'INFO':
        console.info(message, logData.context);
        break;
      case 'WARN':
        console.warn(message, logData.context);
        break;
      case 'ERROR':
        console.error(message, logData.context);
        break;
    }
  }

  // Database logging
  // Routed through the `log-event` Edge Function (service-role insert, caller
  // verified server-side) instead of a direct browser insert — the browser's
  // own session can no longer write to system_logs (2026-08-11, see
  // supabase/migrations/20260811140000_route_system_security_logs_through_server.sql).
  private async logToDatabase(logData: Record<string, unknown>) {
    try {
      const { error } = await supabase.functions.invoke('log-event', {
        body: {
          table: 'system_logs',
          tenant_id: logData.tenantId,
          module_name: logData.moduleOrChannel,
          communication_channel: this.registeredChannels.has(logData.moduleOrChannel as string) ? logData.moduleOrChannel : null,
          category: logData.category || 'general',
          log_level: logData.level,
          message: logData.message,
          context: logData.context || {},
          user_id: logData.userId,
          trace_id: logData.traceId
        }
      });
      if (error) {
        console.warn('Failed to log to database:', error);
      }
    } catch (error) {
      console.warn('Failed to log to database:', error);
    }
  }

  // External services logging (placeholder for DataDog, Splunk, etc.)
  private logToExternalServices(logData: Record<string, unknown>) {
    // Future implementation for DataDog, Splunk, etc.
    console.log('External service logging not yet implemented', logData);
  }

  // Generate trace ID for request tracking
  private generateTraceId(): string {
    return `trace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Convenience methods
  public debug(message: string, context?: unknown, moduleOrChannel?: string) {
    this.log({ level: 'DEBUG', message, context, moduleOrChannel });
  }

  public info(message: string, context?: unknown, moduleOrChannel?: string) {
    this.log({ level: 'INFO', message, context, moduleOrChannel });
  }

  public warn(message: string, context?: unknown, moduleOrChannel?: string) {
    this.log({ level: 'WARN', message, context, moduleOrChannel });
  }

  public error(message: string, context?: unknown, moduleOrChannel?: string) {
    this.log({ level: 'ERROR', message, context, moduleOrChannel });
  }

  // Get registered modules and channels for UI
  public getRegisteredModules(): string[] {
    return Array.from(this.registeredModules);
  }

  public getRegisteredChannels(): string[] {
    return Array.from(this.registeredChannels);
  }

  // Get current configuration for UI
  public getCurrentConfig() {
    return {
      config: this.config,
      categoryLevels: this.categoryLevels,
      channelSettings: this.channelSettings,
      platformConfig: this.platformConfig
    };
  }
}

// Export singleton instance
export const loggingService = LoggingService.getInstance();

// Auto-register known communication channels
loggingService.registerChannel('whatsapp', {
  enabled: true,
  level: 'INFO',
  log_media_events: true,
  log_message_content: false,
  log_status_updates: true
});